import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Loader2, UserPlus, Pencil, Upload, Mail, Send, Trash2, HandHeart, HandCoins, ShieldAlert, Eye } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const WAYS = [
  { key: "prayer", label: "Prayer", icon: HandHeart },
  { key: "financial", label: "Financial", icon: HandCoins },
];

function timeAgo(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return "just now";
  if (s < 86400) { const h = Math.floor(s / 3600); return `${h} hour${h === 1 ? "" : "s"} ago`; }
  const d = Math.floor(s / 86400);
  if (d < 14) return `${d} day${d === 1 ? "" : "s"} ago`;
  if (d < 60) { const w = Math.floor(d / 7); return `${w} week${w === 1 ? "" : "s"} ago`; }
  const mo = Math.floor(d / 30);
  return `${mo} month${mo === 1 ? "" : "s"} ago`;
}


function WayChips({ value = [], onChange }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap gap-2 mt-1" data-testid="partner-ways-chips">
      {WAYS.map((w) => {
        const active = value.includes(w.key);
        const Icon = w.icon;
        return (
          <button
            key={w.key}
            type="button"
            data-testid={`partner-way-${w.key}`}
            aria-pressed={active}
            onClick={() => toggle(w.key)}
            className={`flex items-center gap-1.5 rounded-full px-4 h-10 text-sm font-medium transition-all ${
              active
                ? "bg-[var(--brand-navy)] text-white border border-[var(--brand-navy)]"
                : "bg-white text-[var(--brand-navy)] border border-[var(--brand-navy)]/25 hover:border-[var(--brand-navy)]/60"
            }`}
          >
            <Icon className="h-4 w-4" /> {w.label}
          </button>
        );
      })}
    </div>
  );
}

function WayBadges({ ways = [] }) {
  if (!ways.length) return null;
  const followOnly = ways.includes("follow") && !ways.includes("prayer") && !ways.includes("financial");
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid="partner-way-badges">
      {WAYS.filter((w) => ways.includes(w.key)).map((w) => {
        const Icon = w.icon;
        return (
          <span key={w.key} className="inline-flex items-center gap-1 rounded-full bg-[var(--brand-navy)]/8 text-[var(--brand-navy)] text-[11px] font-semibold px-2 py-0.5">
            <Icon className="h-3 w-3" /> {w.label}
          </span>
        );
      })}
      {followOnly && (
        <span className="inline-flex items-center gap-1 rounded-full bg-[#0E7C86]/12 text-[#0E7C86] text-[11px] font-semibold px-2 py-0.5">
          <Eye className="h-3 w-3" /> Following
        </span>
      )}
    </div>
  );
}

export default function Partners() {
  const { org, refreshOrg } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitingFollowers, setInvitingFollowers] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", partnership_ways: [] });
  const [edit, setEdit] = useState(null);
  const [ef, setEf] = useState({ name: "", email: "", partnership_ways: [] });
  const [savingEdit, setSavingEdit] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [filter, setFilter] = useState("all");
  const [suspected, setSuspected] = useState([]);
  const [cleanOpen, setCleanOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [cleaning, setCleaning] = useState(false);

  const loadSuspected = async () => {
    try { const { data } = await api.get("/partners/suspected-signups"); setSuspected(data.items || []); }
    catch (e) { console.error("Failed to load suspected signups (non-critical):", e); }
  };

  const openCleanup = () => {
    const sel = {}; suspected.forEach((s) => { sel[s.id] = true; }); setSelected(sel); setCleanOpen(true);
  };

  const runCleanup = async () => {
    const ids = suspected.filter((s) => selected[s.id]).map((s) => s.id);
    if (!ids.length) return toast.error("Select at least one to remove");
    setCleaning(true);
    try {
      const { data } = await api.post("/partners/cleanup-signups", { ids });
      toast.success(`Removed ${data.removed} unverified sign-up${data.removed === 1 ? "" : "s"}`);
      setCleanOpen(false);
      await load(); await loadSuspected();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setCleaning(false); }
  };

  const inviteFollowers = async () => {
    setInvitingFollowers(true);
    try {
      const { data } = await api.post("/partners/invite-followers");
      toast.success(`Invite sent to ${data.sent} follower${data.sent === 1 ? "" : "s"} 💛`);
      if (refreshOrg) await refreshOrg();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setInvitingFollowers(false); }
  };

  const doInvite = async () => {
    const emails = inviteEmails.split(/[\s,;]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
    if (!emails.length) return toast.error("Enter at least one valid email");
    setInviting(true); setInviteResult(null);
    try {
      const { data } = await api.post("/partners/invite", { emails });
      setInviteResult(data);
      if (data.invited_count) toast.success(`Invited ${data.invited_count} partner${data.invited_count > 1 ? "s" : ""}`);
      else toast.success("Partner accounts created");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setInviting(false); }
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return [];
    const rows = lines.map((l) => l.split(",").map((c) => c.trim().replace(/^"|"$/g, "")));
    const header = rows[0].map((h) => h.toLowerCase());
    const hasHeader = header.some((h) => h.includes("email"));
    const emailIdx = header.findIndex((h) => h.includes("email"));
    const firstIdx = header.findIndex((h) => h.includes("first"));
    const lastIdx = header.findIndex((h) => h.includes("last"));
    const nameIdx = header.findIndex((h) => h === "name" || h.includes("full"));
    const dataRows = hasHeader ? rows.slice(1) : rows;
    const out = [];
    for (const r of dataRows) {
      let email, name;
      if (hasHeader && emailIdx >= 0) {
        email = r[emailIdx];
        name = nameIdx >= 0 ? r[nameIdx] : [firstIdx >= 0 ? r[firstIdx] : "", lastIdx >= 0 ? r[lastIdx] : ""].filter(Boolean).join(" ");
      } else {
        email = r.find((c) => c.includes("@"));
        name = r.filter((c) => c && !c.includes("@")).join(" ");
      }
      if (email && email.includes("@")) out.push({ name: name || "", email });
    }
    return out;
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const list = parseCsv(text);
      if (!list.length) { toast.error("No valid emails found in that file"); return; }
      const { data } = await api.post("/partners/import", { partners: list });
      setImportResult(data);
      toast.success(`Imported ${data.created_count}, skipped ${data.skipped_count}`);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const openEdit = (p) => { setEf({ name: p.name, email: p.email, partnership_ways: p.partnership_ways || [] }); setEdit(p); };
  const saveEdit = async () => {
    if (!ef.name || !ef.email) return toast.error("Name and email required");
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/partners/${edit.id}`, ef);
      setPartners((prev) => prev.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
      setEdit(null);
      toast.success("Partner updated");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingEdit(false); }
  };

  const deletePartner = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/partners/${delTarget.id}`);
      setPartners((prev) => prev.filter((x) => x.id !== delTarget.id));
      toast.success("Partner removed");
      setDelTarget(null);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setDeleting(false); }
  };

  const load = async () => {
    try { const { data } = await api.get("/partners"); setPartners(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); loadSuspected(); }, []);

  const save = async () => {
    if (!f.name || !f.email || !f.password) return toast.error("All fields required");
    setSaving(true);
    try {
      const { data } = await api.post("/partners", f);
      setPartners([data, ...partners]);
      setF({ name: "", email: "", password: "", partnership_ways: [] });
      setOpen(false);
      toast.success("Partner added");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>;

  const has = (p, k) => (p.partnership_ways || []).includes(k);
  const isFollower = (p) => p.kind === "follower";
  const partnerList = partners.filter((p) => !isFollower(p));
  const followerList = partners.filter(isFollower);
  const matchFilter = (p) => {
    if (filter === "prayer") return has(p, "prayer");
    if (filter === "financial") return has(p, "financial");
    if (filter === "onetime") return !!p.has_onetime;
    if (filter === "monthly") return !!p.has_monthly;
    return true;
  };
  const filtered = filter === "followers" ? followerList : partnerList.filter(matchFilter);
  const FILTERS = [
    { key: "all", label: "All", count: partnerList.length },
    { key: "prayer", label: "Prayer", count: partnerList.filter((p) => has(p, "prayer")).length },
    { key: "financial", label: "Financial", count: partnerList.filter((p) => has(p, "financial")).length },
    { key: "monthly", label: "Monthly", count: partnerList.filter((p) => p.has_monthly).length },
    { key: "onetime", label: "One-time", count: partnerList.filter((p) => p.has_onetime).length },
    ...(followerList.length > 0 ? [{ key: "followers", label: "Followers", count: followerList.length }] : []),
  ];

  return (
    <div>
      <PageHeader title="Partners" />
      <div className="flex items-center justify-end gap-2 mb-3">
        <Button data-testid="invite-partners-btn" onClick={() => { setInviteResult(null); setInviteEmails(""); setInviteOpen(true); }} variant="outline" className="rounded-full border-[var(--tenant-accent)] accent-text"><Mail className="h-4 w-4 mr-1" /> Invite</Button>
        <Button data-testid="import-partners-btn" onClick={() => { setImportResult(null); setImportOpen(true); }} variant="outline" className="rounded-full border-[var(--brand-navy)] text-[var(--brand-navy)]"><Upload className="h-4 w-4 mr-1" /> Import</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-partner-btn" className="rounded-full accent-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-1" /> Add</Button></DialogTrigger>
          <DialogContent className="rounded-3xl max-w-md">
            <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">Add partner</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div><Label className="text-sm">Name</Label><Input data-testid="partner-name-input" className="rounded-xl h-11 mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
              <div><Label className="text-sm">Email</Label><Input data-testid="partner-email-input" type="email" className="rounded-xl h-11 mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
              <div><Label className="text-sm">Temporary password</Label><Input data-testid="partner-password-input" className="rounded-xl h-11 mt-1" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
              <div><Label className="text-sm">How they partner</Label><WayChips value={f.partnership_ways} onChange={(v) => setF({ ...f, partnership_ways: v })} /></div>
              <Button data-testid="partner-submit-btn" disabled={saving} onClick={save} className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">{saving ? "Adding…" : "Add partner"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-sm text-muted-foreground mb-4">Or share join code <span className="font-bold accent-text tracking-widest">{org?.join_code}</span> for self sign-up.</p>

      {suspected.length > 0 && (
        <button
          type="button"
          data-testid="review-signups-banner"
          onClick={openCleanup}
          className="w-full mb-4 flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-left hover:bg-amber-100 transition-colors"
        >
          <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">{suspected.length} unverified sign-up{suspected.length === 1 ? "" : "s"} to review</div>
            <div className="text-xs text-amber-800">These accounts never signed in and have no giving — likely spam. Tap to review &amp; remove.</div>
          </div>
        </button>
      )}

      {partners.length > 0 && (
        <div className="mb-4" data-testid="partners-filter-bar">
          <div className="flex items-baseline gap-2 mb-2.5">
            <span className="font-heading font-bold text-2xl text-[var(--brand-navy)]" data-testid="partners-total">{filter === "followers" ? followerList.length : partnerList.length}</span>
            <span className="text-sm text-muted-foreground">{filter === "followers" ? `follower${followerList.length === 1 ? "" : "s"}` : `partner${partnerList.length === 1 ? "" : "s"} committed to pray or give`}</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {FILTERS.map((ft) => (
              <button
                key={ft.key}
                data-testid={`partners-filter-${ft.key}`}
                onClick={() => setFilter(ft.key)}
                className={`shrink-0 rounded-full px-3.5 h-9 text-sm font-semibold transition-all flex items-center gap-1.5 ${
                  filter === ft.key
                    ? "bg-[var(--brand-navy)] text-white"
                    : "bg-white text-[var(--brand-navy)] border border-[var(--brand-navy)]/20 hover:border-[var(--brand-navy)]/50"
                }`}
              >
                {ft.label}
                <span className={`text-xs rounded-full px-1.5 min-w-[20px] text-center ${filter === ft.key ? "bg-white/20" : "bg-[var(--brand-navy)]/8"}`}>{ft.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {filter === "followers" && followerList.length > 0 && (
          <div className="rounded-2xl p-4 mb-1" style={{ background: "#F7F4EE", border: "1px solid var(--tenant-accent)" }} data-testid="invite-followers-banner">
            <div className="font-heading font-bold text-[var(--brand-navy)]">Invite them to walk closer</div>
            <p className="text-xs text-muted-foreground mt-0.5">Send your {followerList.length} follower{followerList.length === 1 ? "" : "s"} a gentle note inviting them to also pray or give — one tap for them to join.</p>
            <button data-testid="invite-followers-btn" disabled={invitingFollowers} onClick={inviteFollowers}
              className="mt-3 inline-flex items-center gap-2 rounded-full h-10 px-5 text-sm font-semibold text-white transition-transform active:scale-[0.98]" style={{ background: "var(--tenant-accent)" }}>
              {invitingFollowers ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Invite my followers</>}
            </button>
            {org?.followers_last_invited_at && <p className="text-[11px] text-muted-foreground mt-2">Last invited {timeAgo(org.followers_last_invited_at)}. We suggest about once a month.</p>}
          </div>
        )}
        {partners.length === 0 && (
          <EmptyState
            testid="partners-empty"
            icon={UserPlus}
            title="Invite your first partners"
            description="Partners follow your journey, pray, and give. Invite them by email or share your join code below."
            actionLabel="Invite by email"
            onAction={() => { setInviteResult(null); setInviteEmails(""); setInviteOpen(true); }}
            footer={<span className="text-xs text-muted-foreground">or share code <span className="font-bold accent-text tracking-widest">{org?.join_code}</span></span>}
          />
        )}
        {partners.length > 0 && filtered.length === 0 && (
          <div className="text-center py-10 text-sm text-muted-foreground" data-testid="partners-filter-empty">No partners match this filter.</div>
        )}
        {filtered.map((p) => (
          <div key={p.id} className="bg-white rounded-xl p-4 flex items-center justify-between shadow-[0_4px_24px_rgba(44,44,62,0.04)]" data-testid="partner-item">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-[var(--brand-navy)]/10 flex items-center justify-center text-[var(--brand-navy)] font-heading font-bold">{(p.name || "?")[0]}</div>
              <div>
                <div className="font-medium text-[#2C2C3E]">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.email}</div>
                <WayBadges ways={p.partnership_ways} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="font-heading font-bold text-[var(--brand-navy)]">{fmtMoney(p.total_given_cents)}</div>
                <div className="text-xs text-muted-foreground">{p.gift_count} gifts{p.give_clicks > 0 ? ` · ${p.give_clicks} give ${p.give_clicks === 1 ? "tap" : "taps"}` : ""}</div>
              </div>
              <button data-testid="partner-edit-btn" onClick={() => openEdit(p)} className="text-muted-foreground hover:text-[var(--brand-navy)]"><Pencil className="h-4 w-4" /></button>
              <button data-testid="partner-delete-btn" onClick={() => setDelTarget(p)} className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="partner-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[var(--brand-navy)]">Remove partner?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes <b>{delTarget?.name}</b> ({delTarget?.email}) from your partner list. Their past gift records are kept for your finances. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="partner-delete-cancel" className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="partner-delete-confirm" disabled={deleting} onClick={(e) => { e.preventDefault(); deletePartner(); }} className="rounded-full bg-red-600 text-white hover:bg-red-700">
              {deleting ? "Removing…" : "Remove partner"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">Edit partner</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-sm">Name</Label><Input data-testid="partner-edit-name-input" className="rounded-xl h-11 mt-1" value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} /></div>
            <div><Label className="text-sm">Email</Label><Input data-testid="partner-edit-email-input" type="email" className="rounded-xl h-11 mt-1" value={ef.email} onChange={(e) => setEf({ ...ef, email: e.target.value })} /></div>
            <div><Label className="text-sm">How they partner</Label><WayChips value={ef.partnership_ways} onChange={(v) => setEf({ ...ef, partnership_ways: v })} /></div>
            <Button data-testid="partner-edit-save-btn" disabled={savingEdit} onClick={saveEdit} className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">{savingEdit ? "Saving…" : "Save changes"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">Invite partners by email</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">Enter one or more email addresses (comma or line separated). Each person gets a branded invite to follow your journey — they sign in with a one-time code, no password needed.</p>
            <textarea data-testid="invite-emails-input" value={inviteEmails} onChange={(e) => setInviteEmails(e.target.value)} rows={4}
              placeholder={"maria@example.com\njohn@example.com"} className="w-full rounded-xl border p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[var(--tenant-accent)]/40" />
            <Button data-testid="invite-send-btn" disabled={inviting} onClick={doInvite} className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">
              {inviting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sending…</> : <><Send className="h-4 w-4 mr-1" /> Send invites</>}
            </Button>
            {inviteResult && (
              <div className="rounded-xl bg-muted/40 p-4 text-sm space-y-2" data-testid="invite-result">
                {inviteResult.invited_count > 0 && <div className="text-[#2C2C3E]">Sent {inviteResult.invited_count} invite{inviteResult.invited_count > 1 ? "s" : ""}.</div>}
                {inviteResult.pending_count > 0 && <div className="text-xs text-muted-foreground">{inviteResult.pending_count} account{inviteResult.pending_count > 1 ? "s" : ""} created — emails will send once your sending domain is verified.</div>}
                {inviteResult.skipped_count > 0 && <div className="text-xs text-muted-foreground">Skipped (already in another org): {inviteResult.skipped.join(", ")}</div>}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">Import partners</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">Upload a CSV exported from Mailchimp, Google Contacts, or any spreadsheet. We'll read the <b>email</b> and <b>name</b> columns and create partner accounts.</p>
            <label className="flex items-center gap-2 cursor-pointer text-sm rounded-xl border border-dashed p-4 hover:bg-muted/40 transition-colors" data-testid="import-file-label">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 accent-text" />}
              {importing ? "Importing…" : "Choose CSV file"}
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onImportFile} data-testid="import-file-input" disabled={importing} />
            </label>
            {importResult && (
              <div className="rounded-xl bg-muted/40 p-4 text-sm space-y-2" data-testid="import-result">
                <div className="font-semibold text-[#2C2C3E]">Added {importResult.created_count} · skipped {importResult.skipped_count}</div>
                {importResult.created.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Temporary passwords (share with each partner)</div>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {importResult.created.map((c) => (
                        <div key={c.email} className="flex justify-between gap-2 text-xs"><span className="truncate">{c.email}</span><span className="font-mono accent-text">{c.temp_password}</span></div>
                      ))}
                    </div>
                  </div>
                )}
                {importResult.skipped_count > 0 && <div className="text-xs text-muted-foreground">Skipped (already existed): {importResult.skipped.join(", ")}</div>}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cleanOpen} onOpenChange={setCleanOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="cleanup-signups-dialog">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">Review unverified sign-ups</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">These partner accounts <b>never signed in</b> and have <b>no gift history</b> — usually spam bots hitting your public sign-up link. Uncheck anyone you want to keep, then remove the rest.</p>
            <div className="flex items-center justify-between text-xs">
              <button type="button" data-testid="cleanup-select-all" onClick={() => { const s = {}; suspected.forEach((x) => { s[x.id] = true; }); setSelected(s); }} className="accent-text font-semibold hover:underline">Select all</button>
              <button type="button" data-testid="cleanup-select-none" onClick={() => setSelected({})} className="text-muted-foreground hover:underline">Clear</button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1.5 border rounded-xl p-2">
              {suspected.map((s) => (
                <label key={s.id} data-testid="cleanup-item" className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted/40 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`cleanup-check-${s.id}`}
                    checked={!!selected[s.id]}
                    onChange={(e) => setSelected((prev) => ({ ...prev, [s.id]: e.target.checked }))}
                    className="h-4 w-4 accent-[var(--brand-navy)]"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#2C2C3E] truncate">{s.name || s.email}</div>
                    <div className="text-xs text-muted-foreground truncate">{s.email}</div>
                  </div>
                </label>
              ))}
            </div>
            <Button
              data-testid="cleanup-confirm-btn"
              disabled={cleaning}
              onClick={runCleanup}
              className="w-full rounded-full bg-red-600 text-white h-11 hover:bg-red-700"
            >
              {cleaning ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Removing…</> : <><Trash2 className="h-4 w-4 mr-1" /> Remove selected ({suspected.filter((s) => selected[s.id]).length})</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
